import {
  CvImage,
  MarkerPositionData,
  Size,
} from '@modules/NewMaker/FlexiMaker/F/Function/flexmaker.types.ts';
import {
  GANGING_SAFETY_MARGIN_MM,
  MAX_LONG_SIDE_WITH_OFFSET_MM,
  MAX_SHORT_SIDE_WITH_OFFSET_MM,
  MIN_SHORT_SIDE_WITH_OFFSET_MM,
} from '@modules/NewMaker/FlexiMaker/S/Constant/fleximaker';
import { UtilF } from '@modules/Util/F/Function/module/UtilF';

export class FlexiMakerUtils {
  static async createSvgFile(svg_content: string) {
    // SVG 문자열을 Blob으로 변환
    const blob = new Blob([svg_content], { type: 'image/svg+xml' });

    // Blob을 File 객체로 변환
    return new File([blob], 'fleximaker_cutting_line.svg', { type: 'image/svg+xml' });
  }

  static async canvasToPngFile(canvas: HTMLCanvasElement, fileName = 'fleximaker.png'): Promise<File> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas를 Blob으로 변환할 수 없습니다.'));
          return;
        }

        const file = new File([blob], fileName, {
          type: 'image/png',
          lastModified: Date.now(),
        });

        resolve(file);
      }, 'image/png');
    });
  }

  static setCvImageSize(cv_image: CvImage | null, size_info: Size) {
    if (!cv_image?._data) return;

    cv_image._data.flexi_size = {
      ...cv_image._data.flexi_size,
      ...size_info,
    };
  }

  /**
   * 슬라이더 값으로부터 스케일을 계산하는 함수
   * @param slider_value 슬라이더 값 (0~1)
   * @param base_value 기준값
   * @param min_scale 최소 스케일
   * @param max_scale 최대 스케일
   * @returns 계산된 스케일 값
   */
  static calculateScaleFromSlider(slider_value: number, base_value = 0.5, min_scale = 0.5, max_scale = 1.5) {
    const normalized_value = Math.max(0, Math.min(1, slider_value));
    // 축소 영역
    if (normalized_value < base_value) {
      return min_scale + (normalized_value / base_value) * (1.0 - min_scale);
    } else {
      // 확대 영역
      return 1.0 + ((normalized_value - base_value) / (1 - base_value)) * (max_scale - 1.0);
    }
  }

  /**
   * 캔버스 스케일 조정 함수
   * @param target_canvas 대상 캔버스 요소
   * @param scale 적용할 스케일
   * @param center 중심점 좌표
   * @param translate_xy 이동 좌표
   */
  //  참고 G.mp.maker.zoom.adjust_scale = adjust_scale;
  static adjustScale(
    target_canvas: HTMLElement,
    scale: number,
    center: MarkerPositionData,
    translateXY: MarkerPositionData,
  ) {
    const html_el = document.querySelector('html');
    if (html_el) {
      const is_zoomed = scale !== G.mp.maker.editing_canvas().default_ratio;
      is_zoomed ? html_el.setAttribute('zoomed', 'true') : html_el.removeAttribute('zoomed');
    }

    if (target_canvas) {
      target_canvas.style.transform = `translateX(${translateXY.x}px) translateY(${translateXY.y}px) scale(${scale})`;

      target_canvas.style.transformOrigin = `${center.x}px ${center.y}px 0`;
    }
  }

  static sizeGuideTmpl({ width, height }: Size) {
    const element = document.createElement('div');
    element.className = 'flexi-maker-board__size_guide';
    element.textContent =
      G.collabo_type === ''
        ? `${TT('flexi_maker::size_guide', { width: Math.round(width), height: Math.round(height) })}`
        : `${TT('flexi_maker::size_guide_mps', { width: Math.round(width), height: Math.round(height) })}`;

    return element;
  }

  /**
   * 썸네일 캔버스에 여백을 추가하는 함수
   * @param thumbnail_canvas 썸네일 캔버스
   * @returns 여백이 추가된 데이터 URL
   */
  static addMarginThumbnailDataUrl(
    thumbnail_canvas: HTMLCanvasElement | null,
    target_width?: number,
    target_height?: number,
    margins = { top: 100, right: 100, bottom: 100, left: 100 },
  ) {
    if (!thumbnail_canvas) return;

    // 원본 캔버스 크기
    const original_width = thumbnail_canvas.width;
    const original_height = thumbnail_canvas.height;

    // 타겟 크기가 없으면 원본 + 마진만큼의 캔버스 생성
    const new_width = target_width || original_width + margins.left + margins.right;
    const new_height = target_height || original_height + margins.top + margins.bottom;

    // 실제 그릴 수 있는 영역
    const available_width = new_width - margins.left - margins.right;
    const available_height = new_height - margins.top - margins.bottom;

    // 비율 유지하며 원본보다 캔버스가 작아지는 경우도 고려
    // 원본이 들어갈 수 있는 가장 큰 크기 계산
    let draw_width = original_width;
    let draw_height = original_height;

    const original_aspect_ratio = original_width / original_height;
    const available_aspect_ratio = available_width / available_height;

    if (draw_width > available_width || draw_height > available_height) {
      // 캔버스가 원본보다 작은 경우만 축소
      if (original_aspect_ratio > available_aspect_ratio) {
        draw_width = available_width;
        draw_height = available_width / original_aspect_ratio;
      } else {
        draw_height = available_height;
        draw_width = available_height * original_aspect_ratio;
      }
    }

    // 중앙 정렬 위치
    const start_x = margins.left + (available_width - draw_width) / 2;
    const start_y = margins.top + (available_height - draw_height) / 2;

    const new_canvas = document.createElement('canvas');
    new_canvas.width = new_width;
    new_canvas.height = new_height;

    const ctx = new_canvas.getContext('2d');
    if (!ctx) return;

    // ctx.fillStyle = '#f8f9fa';
    // ctx.fillRect(0, 0, new_width, new_height);

    ctx.drawImage(
      thumbnail_canvas,
      0,
      0,
      original_width,
      original_height,
      start_x,
      start_y,
      draw_width,
      draw_height,
    );

    return new_canvas.toDataURL('image/png');
  }
}

export class FlexiMakerDomUtils {
  static switchUploadIcon(has_image: boolean) {
    const maker_frame = document.querySelector('#maker_frame[data-maker_type=FLEXI]');
    if (!maker_frame) return;
    const icon = maker_frame.querySelector('.upload_icon_wrap');
    const desc = maker_frame.querySelector('.image_upload_desc') as HTMLElement;

    if (icon) has_image ? icon.classList.add('has_image') : icon.classList.remove('has_image');
    if (desc) desc.style.display = 'none';
  }

  static toggleMoblieDescription(show: boolean) {
    const text_el = document.querySelector('.flexi-maker-preview__text') as HTMLElement;
    const upload_icon_el = document.querySelector('.flexi-maker-preview__upload-icon') as HTMLElement;
    if (show) {
      text_el?.style?.removeProperty('display');
      upload_icon_el?.style?.removeProperty('display');
    } else {
      text_el?.style?.setProperty('display', 'none', 'important');
      upload_icon_el?.style?.setProperty('display', 'none', 'important');
      const size_guide = document.querySelector('.flexi-maker-board__size_guide') as HTMLElement;
      size_guide?.remove();
    }
  }

  /**
   * 사이즈 가이드 제거
   */
  static removeSizeGuide(): void {
    document.querySelector('.flexi-maker-board__size_guide')?.remove();
  }
}

// 이미지의 aspect ratio 계산
function calculateImageAspectRatio(size: Size): number {
  return size.width / size.height;
}

// 주어진 scale factor로 크기를 조정
function scaleSize(size: Size, scaleFactor: number): Size {
  return {
    width: size.width * scaleFactor,
    height: size.height * scaleFactor,
  };
}

// offset을 포함한 크기 계산
function getSizeWithOffset(size: Size, offset: number): Size {
  return {
    width: size.width + offset,
    height: size.height + offset,
  };
}

// 크기의 짧은 변과 긴 변 구하기
function getShortAndLongSides(size: Size): { shortSide: number; longSide: number } {
  return {
    shortSide: Math.min(size.width, size.height),
    longSide: Math.max(size.width, size.height),
  };
}

// 최소 scale factor 계산 (offset 포함 짧은 변이 MIN_SHORT_SIDE_WITH_OFFSET_MM가 되도록)
function calculateMinScaleFactor(
  imageSize: Size,
  offset: number,
  min_short_side_width_offset_mm = MIN_SHORT_SIDE_WITH_OFFSET_MM,
): number {
  const { shortSide } = getShortAndLongSides(imageSize);
  // (shortSide * scale + offset) = min_short_side_width_offset_mm
  return (min_short_side_width_offset_mm - offset) / shortSide;
}

// 최대 scale factor 계산 (offset 포함 긴 변이 MAX_LONG_SIDE_WITH_OFFSET_MM가 되도록)
function calculateMaxScaleFactorByLongSide(
  imageSize: Size,
  offset: number,
  max_long_side_width_offset_mm = MAX_LONG_SIDE_WITH_OFFSET_MM,
): number {
  const { longSide } = getShortAndLongSides(imageSize);
  // (longSide * scale + offset) = max_long_side_width_offset_mm
  return (max_long_side_width_offset_mm - offset) / longSide;
}

// 최대 scale factor 계산 (offset 포함 짧은 변이 MAX_SHORT_SIDE_WITH_OFFSET_MM가 되도록)
function calculateMaxScaleFactorByShortSide(
  imageSize: Size,
  offset: number,
  max_short_side_width_offset_mm = MAX_SHORT_SIDE_WITH_OFFSET_MM,
): number {
  const { shortSide } = getShortAndLongSides(imageSize);
  // (shortSide * scale + offset) = max_short_side_width_offset_mm
  return (max_short_side_width_offset_mm - offset) / shortSide;
}

// 최대 scale factor 계산 (긴 변과 짧은 변 제약을 모두 고려)
function calculateMaxScaleFactor(
  imageSize: Size,
  offset: number,
  max_long_side_width_offset_mm = MAX_LONG_SIDE_WITH_OFFSET_MM,
  max_short_side_width_offset_mm = MAX_SHORT_SIDE_WITH_OFFSET_MM,
): number {
  const maxScaleByLongSide = calculateMaxScaleFactorByLongSide(
    imageSize,
    offset,
    max_long_side_width_offset_mm,
  );
  const maxScaleByShortSide = calculateMaxScaleFactorByShortSide(
    imageSize,
    offset,
    max_short_side_width_offset_mm,
  );

  // 두 제약 조건 중 더 작은 값을 선택 (더 제한적인 조건)
  return Math.min(maxScaleByLongSide, maxScaleByShortSide);
}

// 이미지 종횡비 호환성 검증 함수
function validateImageCompatibilityWithConstraints(
  imageSize: Size,
  offset: number,
  min_short_side_width_offset_mm = MIN_SHORT_SIDE_WITH_OFFSET_MM, // 조판 제한 기준
  max_long_side_width_offset_mm = MAX_LONG_SIDE_WITH_OFFSET_MM, // 조판 제한 기준
  max_short_side_width_offset_mm = MAX_SHORT_SIDE_WITH_OFFSET_MM, // 조판 제한 기준
): void {
  const { shortSide, longSide } = getShortAndLongSides(imageSize);
  const imageAspectRatio = longSide / shortSide;

  //  조판에서 허용 가능한 최대 종횡비 계산
  // shortSide가 최소 조건을 만족할 때의 longSide 최대값
  const maxPossibleLongSideWhenShortSideIsMin =
    ((min_short_side_width_offset_mm - offset) / shortSide) * longSide + offset;

  // longSide가 최대 조건을 만족할 때의 shortSide 최소값
  const minPossibleShortSideWhenLongSideIsMax =
    ((max_long_side_width_offset_mm - offset) / longSide) * shortSide + offset;

  // shortSide가 최대 조건을 만족할 때의 longSide 최소값
  const minPossibleLongSideWhenShortSideIsMax =
    ((max_short_side_width_offset_mm - offset) / shortSide) * longSide + offset;

  // 모순 상황 감지: 종횡비가 너무 극단적인 경우
  if (maxPossibleLongSideWhenShortSideIsMin > max_long_side_width_offset_mm) {
    throw new Error(
      `이미지 비율 (${imageAspectRatio.toFixed(2)}:1)이 너무 극단적입니다. ` +
        `최소 짧은 변 요구사항 (${min_short_side_width_offset_mm}mm)을 충족하려면, ` +
        `긴 변이 ${maxPossibleLongSideWhenShortSideIsMin.toFixed(1)}mm가 되어 ` +
        `최대 긴 변 제한 (${max_long_side_width_offset_mm}mm)을 초과합니다.`,
    );
  }

  if (minPossibleShortSideWhenLongSideIsMax < min_short_side_width_offset_mm) {
    throw new Error(
      `이미지 비율 (${imageAspectRatio.toFixed(2)}:1)이 너무 극단적입니다. ` +
        `최대 긴 변 요구사항 (${max_long_side_width_offset_mm}mm)을 충족하려면, ` +
        `짧은 변이 ${minPossibleShortSideWhenLongSideIsMax.toFixed(1)}mm가 되어 ` +
        `최소 짧은 변 제한 (${min_short_side_width_offset_mm}mm)보다 작아집니다.`,
    );
  }

  // 짧은 변 최대 제약 조건 검증
  if (maxPossibleLongSideWhenShortSideIsMin > minPossibleLongSideWhenShortSideIsMax) {
    throw new Error(
      `이미지 비율 (${imageAspectRatio.toFixed(2)}:1)이 너무 극단적입니다. ` +
        `최소 짧은 변 요구사항과 최대 짧은 변 제한을 동시에 만족할 수 없습니다. ` +
        `최소 짧은 변 조건에서 긴 변: ${maxPossibleLongSideWhenShortSideIsMin.toFixed(1)}mm, ` +
        `최대 짧은 변 조건에서 긴 변: ${minPossibleLongSideWhenShortSideIsMax.toFixed(1)}mm`,
    );
  }
}

// 주어진 이미지 크기와 offset으로 최소/최대 scale factor일 때의 정보 계산
export function getMinMaxShortLongSize(
  imageSize: Size,
  offset: number,
  min_short_side_width_offset_mm = MIN_SHORT_SIDE_WITH_OFFSET_MM,
  max_long_side_width_offset_mm = MAX_LONG_SIDE_WITH_OFFSET_MM,
  max_short_side_width_offset_mm = MAX_SHORT_SIDE_WITH_OFFSET_MM,
  margin_mm = GANGING_SAFETY_MARGIN_MM,
): {
  shortSide: { min: number; max: number };
  longSide: { min: number; max: number };
} {
  // 입력 검증
  if (imageSize.width <= 0 || imageSize.height <= 0) {
    throw new Error('이미지 크기는 양수 값의 가로와 세로를 가져야 합니다');
  }
  if (offset < 0) {
    throw new Error('오프셋은 음수가 될 수 없습니다');
  }

  // offset이 너무 커서 제약 조건을 만족할 수 없는 경우 검증 (margin을 적용한 관대한 검증)
  const lenient_min_short_side = min_short_side_width_offset_mm;
  const lenient_max_long_side = max_long_side_width_offset_mm;
  const lenient_max_short_side = max_short_side_width_offset_mm;

  if (offset >= lenient_min_short_side) {
    throw new Error(
      `오프셋 (${offset}mm)이 너무 큽니다. 최소 짧은 변 요구사항 (${min_short_side_width_offset_mm}mm)보다 작아야 합니다.`,
    );
  }
  if (offset >= lenient_max_long_side) {
    throw new Error(
      `오프셋 (${offset}mm)이 너무 큽니다. 최대 긴 변 요구사항 (${max_long_side_width_offset_mm}mm)보다 작아야 합니다.`,
    );
  }
  if (offset >= lenient_max_short_side) {
    throw new Error(
      `오프셋 (${offset}mm)이 너무 큽니다. 최대 짧은 변 요구사항 (${max_short_side_width_offset_mm}mm)보다 작아야 합니다.`,
    );
  }

  // 이미지 종횡비와 제약 조건 호환성 검증 (margin을 적용한 관대한 검증)
  validateImageCompatibilityWithConstraints(
    imageSize,
    offset,
    min_short_side_width_offset_mm,
    max_long_side_width_offset_mm,
    max_short_side_width_offset_mm,
  );

  calculateImageAspectRatio(imageSize);

  // 최소 scale factor: offset 포함 짧은 변이 MIN_SHORT_SIDE_WITH_OFFSET_MM가 되도록
  const minScaleFactor = calculateMinScaleFactor(
    imageSize,
    offset,
    min_short_side_width_offset_mm + margin_mm,
  );

  // 최대 scale factor: offset 포함 긴 변이 MAX_LONG_SIDE_WITH_OFFSET_MM가 되도록
  const maxScaleFactor = calculateMaxScaleFactor(
    imageSize,
    offset,
    max_long_side_width_offset_mm - margin_mm,
    max_short_side_width_offset_mm - margin_mm,
  );

  // scale factor 유효성 검증 (이론적으로는 위의 검증에서 걸러져야 하지만 이중 안전장치)
  if (minScaleFactor <= 0) {
    throw new Error(
      `유효하지 않은 최소 스케일 팩터: ${minScaleFactor}. 오프셋 (${offset})이 너무 클 수 있습니다.`,
    );
  }
  if (maxScaleFactor <= 0) {
    throw new Error(
      `유효하지 않은 최대 스케일 팩터: ${maxScaleFactor}. 오프셋 (${offset})이 너무 클 수 있습니다.`,
    );
  }
  if (minScaleFactor > maxScaleFactor) {
    throw new Error(
      `스케일 팩터 충돌이 감지되었습니다. 이미지 비율이 크기 제약 조건과 호환되지 않습니다. ` +
        `최소 스케일 팩터 (${minScaleFactor.toFixed(3)}) > 최대 스케일 팩터 (${maxScaleFactor.toFixed(3)})`,
    );
  }

  // 최소 scale에서의 크기 (offset 포함)
  const minScaledSize = scaleSize(imageSize, minScaleFactor);
  const minScaleSizeWithOffset = getSizeWithOffset(minScaledSize, offset);
  const { shortSide: minScaleShortSideWithOffset, longSide: minScaleLongSideWithOffset } =
    getShortAndLongSides(minScaleSizeWithOffset);

  // 최대 scale에서의 크기 (offset 포함)
  const maxScaledSize = scaleSize(imageSize, maxScaleFactor);
  const maxScaleSizeWithOffset = getSizeWithOffset(maxScaledSize, offset);
  const { shortSide: maxScaleShortSideWithOffset, longSide: maxScaleLongSideWithOffset } =
    getShortAndLongSides(maxScaleSizeWithOffset);

  return {
    shortSide: {
      min: minScaleShortSideWithOffset,
      max: maxScaleShortSideWithOffset,
    },
    longSide: {
      min: minScaleLongSideWithOffset,
      max: maxScaleLongSideWithOffset,
    },
  };
}

/**
 * 선형 보간(Linear Interpolation) 함수
 * @param start 시작값
 * @param end 끝값
 * @param t 보간 비율 (0~1)
 * @returns 보간된 값
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

/**
 * 사이즈 범위 매핑 함수를 생성하는 커리 함수
 * @param shortSide 짧은 변 범위
 * @param longSide 긴 변 범위
 * @returns 0~1 범위의 입력값을 받아 보간된 { shortSide, longSide } 값을 반환하는 함수
 */
export function createSizeMapper(
  shortSide: { min: number; max: number },
  longSide: { min: number; max: number },
) {
  return function mapInputToSize(inputValue: number): { shortSide: number; longSide: number } {
    // 입력값을 0~1 범위로 제한
    const normalizedInput = Math.max(0, Math.min(1, inputValue));

    return {
      shortSide: lerp(shortSide.min, shortSide.max, normalizedInput),
      longSide: lerp(longSide.min, longSide.max, normalizedInput),
    };
  };
}

/**
 * @param {number} slider_value - 0 ~ 1
 * @returns {{ thickness: number, dashA: number, dashB: number }}
 */
export function getStrokeStyle(slider_value: number): { thickness: number; dashA: number; dashB: number } {
  let thickness, dashA, dashB;
  const t = slider_value;

  if (UtilF.isLegacyMobile()) {
    if (t <= 0.5) {
      // 0~0.5 : 6~7, 18~22, 20~24
      const p = t / 0.5;
      thickness = lerp(6, 7, p);
      dashA = lerp(18, 22, p);
      dashB = lerp(20, 24, p);
    } else {
      // 0.5~1 : 7~8, 22~26, 24~30
      const p = (t - 0.5) / 0.5;
      thickness = lerp(7, 8, p);
      dashA = lerp(22, 26, p);
      dashB = lerp(24, 30, p);
    }
  } else {
    thickness = 3;
    if (t <= 0.5) {
      // 0~0.5 : [10,12]~[12,14]
      const p = t / 0.5;
      dashA = lerp(10, 12, p);
      dashB = lerp(12, 14, p);
    } else {
      // 0.5~1 : [12,14]~[15,16]
      const p = (t - 0.5) / 0.5;
      dashA = lerp(12, 15, p);
      dashB = lerp(14, 16, p);
    }
  }
  return { thickness, dashA, dashB };
}
